import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { hasPermission, type AppPermission } from "@/lib/permissions";

type ProtectedRole = "ADMIN" | "PROFESSIONAL" | "SUPER_ADMIN";

function isSuperAdminRole(role?: string | null) {
  return role?.toUpperCase() === "SUPER_ADMIN";
}

function normalizeRole(role?: string | null): ProtectedRole | null {
  const normalizedRole = role?.toUpperCase();

  if (normalizedRole === "ADMIN" || normalizedRole === "PROFESSIONAL" || normalizedRole === "SUPER_ADMIN") {
    return normalizedRole;
  }

  return null;
}

function resolveUnauthorizedRedirect(role: ProtectedRole | null) {
  return role === "SUPER_ADMIN" ? "/super-admin" : "/dashboard";
}

function LoadingScreen() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen flex items-center justify-center bg-clinic-bg">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-clinic-primary/30 border-t-clinic-primary rounded-full animate-spin mx-auto mb-4" />
        <p className="text-clinic-text-secondary">{t("Carregando...")}</p>
      </div>
    </div>
  );
}

function PermissionDeniedScreen() {
  const { t } = useLanguage();

  return (
    <div className="flex min-h-screen items-center justify-center bg-clinic-bg p-6">
      <div className="max-w-md rounded-2xl border border-clinic-border bg-clinic-surface p-6 text-center shadow-sm">
        <h1 className="font-heading text-2xl font-bold text-clinic-text">{t("Acesso bloqueado")}</h1>
        <p className="mt-2 text-clinic-text-secondary">
          {t("Seu perfil atual não possui permissão para acessar esta página.")}
        </p>
      </div>
    </div>
  );
}

export function ProtectedRoute({
  children,
  allowedRoles,
  requiredPermission,
}: {
  children: ReactNode;
  allowedRoles?: ProtectedRole[];
  requiredPermission?: AppPermission;
}) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const userRole = normalizeRole(user.role);

  if (allowedRoles && (!userRole || !allowedRoles.includes(userRole))) {
    return <Navigate to={resolveUnauthorizedRedirect(userRole)} replace />;
  }

  if (requiredPermission && !hasPermission(user, requiredPermission)) {
    return <PermissionDeniedScreen />;
  }

  return <>{children}</>;
}

export function PublicRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to={isSuperAdminRole(user.role) ? "/super-admin" : "/dashboard"} replace />;
  }

  return <>{children}</>;
}
